<template>
  <!-- A paleta `--md-*` que esta tela consome é global (scss/style.scss), chaveada
       por `html.is-dark`. Declarar aqui deixaria de fora as sheets que esta tela
       abre pelo host do AppLayout, que é irmão do <router-view>. -->
  <main class="my-downloads" :class="{ 'my-downloads--has-other': otherDownloads.length }">
    <div
      v-if="!isConnected"
      class="offline-banner"
      :style="{ borderColor: $theme.primaryColor, color: $theme.primaryColor }"
    >
      Você está offline. Apenas conteúdo baixado está disponível.
    </div>

    <MyDownloadsHeader
      @back="goBack" 
      :total-learning-objects="totalDownloadedLearningObjects"
      :total-courses="totalDownloadedCourses"
      :total-bytes="totalDownloadedBytes"
      @open-settings="openSettingsSheet"
    />

    <template v-if="totalDownloadedLearningObjects > 0">
      <MyDownloadsFilterTabs v-model="filter" :total-learning-objects="totalDownloadedLearningObjects" />

      <ContinueWatchingRow
        v-if="filter === 'all' && continueWatchingLearningObjects.length"
        :learning-objects="continueWatchingLearningObjects"
        @play="playLearningObject"
      />

      <section class="by-course" v-if="myDownloads.length || !otherDownloads.length">
        <h2 class="section-title">Por curso</h2>
        <template v-if="myDownloads.length">
          <DownloadedCourseCard
            v-for="course in myDownloads"
            :key="course.enrollId"
            :course="course"
            :filter="filter"
            :expanded="expandedCourseId === course.enrollId"
            @toggle="toggleCourse"
            @play="playLearningObject"
            @open-actions="openActionsSheet"
            @open-course-actions="openCourseActionsSheet"
            @open-group-actions="openGroupActionsSheet"
          />
        </template>
        <p v-else class="empty-filter">Nenhuma aula corresponde ao filtro selecionado.</p>
      </section>

      <div class="find-more">
        <button
          type="button"
          class="find-more__btn"
          :style="{ color: $theme.primaryColor, borderColor: $theme.primaryColor }"
          @click="$router.push('/p/my-courses')"
        >
          <i class="material-icons">explore</i>
          <span>Encontrar mais cursos para baixar</span>
        </button>
      </div>

      <div v-if="otherDownloads.length" class="other-sheet">
        <section class="other-sheet__panel">
          <button type="button" class="other-downloads__toggle" @click="expandedOtherSection = !expandedOtherSection">
            <span class="other-downloads__heading">
              <span class="section-title">Outros downloads neste dispositivo</span>
              <span class="other-downloads__summary">{{ otherDownloadsSummary }}</span>
            </span>
            <i class="material-icons">{{ expandedOtherSection ? 'expand_more' : 'expand_less' }}</i>
          </button>
          <transition name="other-sheet-body">
            <div v-if="expandedOtherSection" class="other-sheet__body">
              <DownloadedCourseCard
                v-for="course in otherDownloads"
                :key="course.enrollId"
                :course="course"
                :filter="filter"
                :expanded="expandedCourseId === course.enrollId"
                @toggle="toggleCourse"
                @play="playLearningObject"
                @open-actions="openActionsSheet"
                @open-course-actions="openCourseActionsSheet"
                @open-group-actions="openGroupActionsSheet"
              />
            </div>
          </transition>
        </section>
      </div>
    </template>

    <EmptyDownloads v-else />

    <LearningObjectActionsSheet
      :visible="actionsSheetVisible"
      :learning-object="actionsSheetLearningObject"
      :has-access="actionsSheetHasAccess"
      @close="actionsSheetVisible = false"
      @play="playLearningObject"
      @remove="onRequestRemove"
    />
    <CourseActionsSheet
      :visible="courseSheetVisible"
      :course="courseSheetCourse"
      @close="courseSheetVisible = false"
      @open-course="openCourse"
      @remove-all="onRequestRemoveCourse"
    />
    <GroupActionsSheet
      :visible="groupSheetVisible"
      :group="groupSheetGroup"
      @close="groupSheetVisible = false"
      @remove-all="onRequestRemoveGroup"
    />
    <DownloadSettingsSheet
      :visible="settingsSheetVisible"
      :current-quality="downloadQualityWifi"
      @edit-quality="onEditQualityFromSettings"
      @open-diagnostics="onOpenDiagnosticsFromSettings"
      @close="settingsSheetVisible = false"
    />
    <DownloadDiagnosticsSheet
      :visible="diagnosticsSheetVisible"
      @close="diagnosticsSheetVisible = false"
    />

    <LeaseDebugPanel v-if="showLeaseDebug" />
  </main>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import MyDownloadsHeader from '@/components/myDownloads/MyDownloadsHeader.vue';
import downloadActions from '@/mixins/downloadActions';
import MyDownloadsFilterTabs from '@/components/myDownloads/MyDownloadsFilterTabs.vue';
import ContinueWatchingRow from '@/components/myDownloads/ContinueWatchingRow.vue';
import DownloadedCourseCard from '@/components/myDownloads/DownloadedCourseCard.vue';
import EmptyDownloads from '@/components/myDownloads/EmptyDownloads.vue';
import LearningObjectActionsSheet from '@/components/myDownloads/LearningObjectActionsSheet.vue';
import CourseActionsSheet from '@/components/myDownloads/CourseActionsSheet.vue';
import GroupActionsSheet from '@/components/myDownloads/GroupActionsSheet.vue';
import DownloadSettingsSheet from '@/components/DownloadSettingsSheet.vue';
import DownloadDiagnosticsSheet from '@/components/DownloadDiagnosticsSheet.vue';
import LeaseDebugPanel from '@/components/dev/LeaseDebugPanel.vue';
import { formatBytes } from '@/services/download/downloadHelpers';

export default {
  name: 'MyDownloads',
  mixins: [downloadActions],
  components: {
    MyDownloadsHeader,
    MyDownloadsFilterTabs,
    ContinueWatchingRow,
    DownloadedCourseCard,
    EmptyDownloads,
    LearningObjectActionsSheet,
    CourseActionsSheet,
    GroupActionsSheet,
    DownloadSettingsSheet,
    DownloadDiagnosticsSheet,
    LeaseDebugPanel
  },
  // `from.name` é null quando a tela foi a rota de ENTRADA (deep link, boot do
  // app direto aqui). Nesse caso `$router.back()` sairia do app; o goBack cai
  // pra listagem de cursos.
  beforeRouteEnter (to, from, next) {
    next(vm => { vm.cameFromApp = from.name != null; });
  },

  data() {
    return {
      filter: 'all',
      expandedCourseId: null,
      cameFromApp: false,
      actionsSheetVisible: false,
      actionsSheetLearningObject: null,
      // Indica se a aula da sheet aberta atualmente está em curso com acesso
      // ativo. Propagado pelo payload do `open-actions` do LearningObjectRow.
      // Default true mantém o comportamento original quando o caller é antigo.
      actionsSheetHasAccess: true,
      courseSheetVisible: false,
      courseSheetCourse: null,
      groupSheetVisible: false,
      groupSheetGroup: null,
      settingsSheetVisible: false,
      diagnosticsSheetVisible: false,
      // Seção "Outros downloads neste dispositivo" (baixados por outra conta)
      // começa colapsada.
      expandedOtherSection: false
    };
  },
  computed: {
    ...mapGetters({
      isConnected: 'dom/isConnected',
      downloadedCourses: 'download/downloadedCourses',
      continueWatchingLearningObjects: 'download/continueWatchingLearningObjects',
      totalDownloadedLearningObjects: 'download/totalDownloadedLearningObjects',
      totalDownloadedCourses: 'download/totalDownloadedCourses',
      totalDownloadedBytes: 'download/totalDownloadedBytes',
      getLocalPosition: 'download/getLocalPosition'
    }),
    ...mapState({
      downloadQualityWifi: (state) => state.download.downloadQualityWifi
    }),
    // Painel de debug do lease: só equipe interna, e só com o toggle de
    // diagnóstico ligado nas Configurações de download.
    showLeaseDebug() {
      return this.$isInternalUser && this.$store.getters['download/getShowOfflineSyncPanel'];
    },
    filteredCourses() {
      if (this.filter === 'all') return this.downloadedCourses;
      return this.downloadedCourses
        .map(course => {
          const groups = course.groups
            .map(group => ({
              ...group,
              learningObjects: group.learningObjects.filter(lo => this.learningObjectMatchesFilter(course.enrollId, lo))
            }))
            .filter(group => group.learningObjects.length > 0);
          if (!groups.length) return null;
          return { ...course, groups };
        })
        .filter(Boolean);
    },
    // Downloads da conta logada (+ legados sem dono) — seção principal.
    myDownloads() {
      return this.filteredCourses.filter(c => !c.isOtherAccount);
    },
    // Downloads baixados por OUTRA conta no mesmo device — seção colapsável,
    // pra permitir liberar espaço sem precisar logar em cada conta. Playback
    // segue bloqueado pelo lease (studentId não bate).
    otherDownloads() {
      return this.filteredCourses.filter(c => c.isOtherAccount);
    },
    otherDownloadsSummary() {
      const courses = this.otherDownloads.length;
      const bytes = this.otherDownloads.reduce((acc, c) => acc + (c.totalSize || 0), 0);
      const cursoLabel = courses === 1 ? 'curso' : 'cursos';
      return bytes > 0 ? `${courses} ${cursoLabel} · ${formatBytes(bytes)}` : `${courses} ${cursoLabel}`;
    }
  },
  watch: {
    // Se a seção "Outros downloads" esvaziar (user removeu tudo da outra conta),
    // reseta o estado expandido pra não reabrir já aberta se reaparecer.
    'otherDownloads.length'(len) {
      if (!len) this.expandedOtherSection = false;
    }
  },
  mounted() {
    this.$store.dispatch('enroll/clearCurrentEnroll');
    this.$updateTheme({
      darkTheme: false,
      primary: '#0564b1',
      secondary: '#ffffff'
    });
    if (this.$gtag) {
      this.$gtag.pageview({ page_title: 'meus downloads', page_path: '/p/my-downloads' });
    }
    // Rehidrata positions a partir do grid cacheado pra cada enroll com
    // download. Cobre o caso em que o user abre Meus Downloads SEM ter
    // passado por um curso recentemente — sem isso, syncPositionsFromGrid
    // só rodaria quando ele clicasse em baixar uma nova aula. Idempotente:
    // chamadas extras são no-op se positions já está atualizada.
    this._syncAllDownloadedEnrolls();
  },
  methods: {
    _syncAllDownloadedEnrolls() {
      // Pra cada enroll com pelo menos 1 LO baixado, rehidrata positions
      // usando o grid persistido em offlineStorage. Reaproveita a action
      // syncPositionsFromGrid do store, que tem a política de overwrite
      // (não sobrescreve play real do user com estimativa).
      const downloads = this.$store.state.download.downloads || {};
      Object.keys(downloads).forEach(enrollId => {
        const enrollState = downloads[enrollId];
        if (!enrollState || !enrollState.learningObjects) return;
        const hasAnyDownloaded = Object.values(enrollState.learningObjects)
          .some(status => status === 'downloaded');
        if (!hasAnyDownloaded) return;
        // import dinâmico evita acoplar página a serviço de baixo nível
        import('@/services/offline/offlineStorage').then(({ offlineStorage }) => {
          const grid = offlineStorage.getGrid(enrollId);
          if (!grid) return;
          this.$store.dispatch('download/syncPositionsFromGrid', { enrollId, grid });
        });
      });
    },
    openSettingsSheet() {
      this.settingsSheetVisible = true;
    },
    onEditQualityFromSettings() {
      // Fecha o sheet de Configurações ANTES de abrir o de qualidade, com
      // pequeno delay pra evitar overlap visual de dois backdrops + dois
      // slide-ups concorrentes. 260ms = duração do slide do sheet.
      this.settingsSheetVisible = false;
      setTimeout(() => { this.editDefaultQuality(this.downloadQualityWifi); }, 260);
    },
    onOpenDiagnosticsFromSettings() {
      // Gate funcional: a opção já é escondida pra não-interno no sheet, mas o
      // evento poderia chegar por outro caminho (caller programático/legado).
      if (!this.$isInternalUser) return;
      this.settingsSheetVisible = false;
      setTimeout(() => { this.diagnosticsSheetVisible = true; }, 260);
    },
    learningObjectMatchesFilter(enrollId, lo) {
      const pos = this.getLocalPosition(enrollId, lo.id);
      const completed = lo.duration && pos >= lo.duration - 3;
      if (this.filter === 'completed') return !!completed;
      if (this.filter === 'inProgress') return pos > 0 && !completed;
      return true;
    },
    toggleCourse(enrollId) {
      this.expandedCourseId = this.expandedCourseId === enrollId ? null : enrollId;
    },
    goBack() {
      if (this.cameFromApp) this.$router.back();
      else this.$router.push('/p/my-courses');
    },
    playLearningObject({ enrollId, loId }) {
      // Gate funcional final — defesa em profundidade pra caso algum
      // ponto da árvore deixar o evento `play` escapar mesmo sem acesso.
      // Hoje os filhos já bloqueiam via :disabled + guard no onPlayClick,
      // mas se um caller programático (ex: deep link, ContinueWatching)
      // chamar direto, esse check protege.
      const course = (this.downloadedCourses || []).find(c => String(c.enrollId) === String(enrollId));
      // Baixado por outra conta no mesmo device — o lease não bate pro user
      // logado, então o playback seria bloqueado com um modal de "expirado"
      // confuso. Avisa claro e oferece remover (liberar espaço).
      if (course && course.isOtherAccount) {
        this.$store.commit('toast/NEW', {
          type: 'warning',
          message: 'Baixado em outra conta. Entre nela para assistir, ou remova para liberar espaço.',
          suppressOffline: true
        });
        return;
      }
      if (course && course.hasAccess === false) {
        this.$store.commit('toast/NEW', {
          type: 'warning',
          message: 'Esta aula não está mais acessível. Você pode remover o download pra liberar espaço.',
          suppressOffline: true
        });
        return;
      }
      this.$router.push({ name: 'class', params: { enrollId, id: loId } });
    },
    openActionsSheet(payload) {
      this.actionsSheetLearningObject = payload;
      // Payload novo do LearningObjectRow inclui `hasAccess`. Caller antigo
      // (sem o campo) → assume true (retrocompat).
      this.actionsSheetHasAccess = payload && payload.hasAccess !== false;
      this.actionsSheetVisible = true;
    },
    openCourseActionsSheet(payload) {
      this.courseSheetCourse = payload;
      this.courseSheetVisible = true;
    },
    openGroupActionsSheet(payload) {
      this.groupSheetGroup = payload;
      this.groupSheetVisible = true;
    },
    async onRequestRemoveGroup({ enrollId, name, loIds }) {
      if (await this.confirmRemoveDownload(name)) {
        await this.doRemoveGroup({ enrollId, loIds });
      }
    },
    async doRemoveGroup({ enrollId, loIds }) {
      try {
        await this.$store.dispatch('download/removeAllFromGroup', { enrollId, loIds });
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: 'Downloads removidos.',
          duration: 2500
        });
      } catch (e) {
        this.$store.commit('toast/NEW', {
          type: 'error',
          message: 'Não foi possível remover os downloads.',
          duration: 3500
        });
      }
    },
    openCourse({ enrollId }) {
      // Defesa em profundidade: o botão "Acessar curso" no CourseActionsSheet
      // já está com v-if="hasAccess", então não deveria emitir esse evento
      // pra curso revogado. Mas se algum caller programático chamar (deep
      // link, debug), gate aqui evita navegar pra curso sem acesso.
      const course = (this.downloadedCourses || []).find(c => String(c.enrollId) === String(enrollId));
      if (course && course.hasAccess === false) {
        this.$store.commit('toast/NEW', {
          type: 'warning',
          message: 'Este curso não está mais acessível.',
          suppressOffline: true
        });
        return;
      }
      this.$router.push({ name: 'course', params: { id: enrollId } });
    },
    async onRequestRemoveCourse({ enrollId, name }) {
      // Snapshot do nome antes de abrir o confirm — depois do dispatch o card
      // do curso some da lista e o sheet field zera. Mantém o título no modal
      // mesmo se o re-render já tiver removido o curso visualmente.
      if (await this.confirmRemoveDownload(name)) {
        await this.doRemoveCourse(enrollId);
      }
    },
    async doRemoveCourse(enrollId) {
      try {
        // Se o curso aberto está sendo removido, colapsa pra evitar que o
        // chevron continue apontando pra um card que vai sumir.
        if (this.expandedCourseId === enrollId) this.expandedCourseId = null;
        await this.$store.dispatch('download/removeAllFromEnroll', { enrollId });
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: 'Downloads do curso removidos.',
          duration: 2500
        });
      } catch (e) {
        this.$store.commit('toast/NEW', {
          type: 'error',
          message: 'Não foi possível remover os downloads do curso.',
          duration: 3500
        });
      }
    },
    async onRequestRemove({ enrollId, loId }) {
      // Captura o nome do LO ANTES de abrir o confirm — depois que o user
      // confirma a remoção, o getter pode já não devolver mais o LO (race
      // entre dispatch e re-render), e o modal de confirmação ficaria sem
      // título. Snapshot resolve.
      const name = this.actionsSheetLearningObject && this.actionsSheetLearningObject.name;
      if (await this.confirmRemoveDownload(name)) {
        await this.doRemove({ enrollId, loId });
      }
    },
    async doRemove({ enrollId, loId }) {
      try {
        await this.$store.dispatch('download/removeLearningObject', { enrollId, id: loId });
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: 'Download removido.',
          duration: 2500
        });
      } catch (e) {
        this.$store.commit('toast/NEW', {
          type: 'error',
          message: 'Não foi possível remover o download.',
          duration: 3500
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.my-downloads {
  min-height: 100vh;
  background: var(--md-bg);
  color: var(--md-text);
  padding-bottom: 32px;
}

.offline-banner {
  margin: 12px 18px 0;
  padding: 10px 14px;
  border: 1px solid;
  border-radius: 10px;
  background: var(--md-surface-elev);
  font-size: 12px;
  font-weight: 500;
}

.by-course {
  margin-top: 8px;
}

.section-title {
  margin: 4px 18px 12px;
  font-size: 16px;
  font-weight: 600;
  color: var(--md-text);
}

.empty-filter {
  margin: 24px 18px;
  color: var(--md-text-dim);
  font-size: 13px;
  text-align: center;
}

// Bottom sheet "Outros downloads neste dispositivo": fechado = barra fixa no
// rodapé; aberto = sobe como painel rolável com backdrop.
// Escape hatch pra listagem de cursos, no espírito do "Find More to Download"
// do Netflix. Fantasma de propósito: nesta tela a ação principal é consumir o
// que já foi baixado — um sólido na cor primária competiria com os cards e
// repetiria o peso do CTA do estado vazio, que ali sim é a ação principal.
// Logo depois da lista, no fluxo normal. Já esteve preso ao rodapé da viewport
// via `margin-top: auto`, mas o <main> começa abaixo do header do app: com
// `min-height: 100vh` o botão caía uma altura de header ABAIXO da dobra e
// ficava invisível sem rolar — o oposto do objetivo, que é ser descoberto.
.find-more {
  display: flex;
  justify-content: center;
  padding: 24px 18px 4px;
}

.find-more__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid;
  border-radius: 999px;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;

  .material-icons {
    font-size: 18px;
  }
}

.my-downloads--has-other {
  // Reserva o espaço da barra fixa pra o último card de "Por curso" não ficar
  // escondido atrás dela.
  padding-bottom: calc(76px + env(safe-area-inset-bottom));
}

.other-sheet__panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 61;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  background: var(--md-surface);
  border-top: 1px solid var(--md-border-strong);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.14);
  padding-bottom: env(safe-area-inset-bottom);
}

.other-sheet__body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 12px;
}

// Painel cresce/encolhe pra cima suavemente (bottom ancorado no rodapé). Anima
// max-height + opacity; overflow hidden durante a transição evita flicker de
// scrollbar antes do estado final assumir o overflow-y: auto.
.other-sheet-body-enter-active,
.other-sheet-body-leave-active {
  transition: max-height 280ms ease, opacity 220ms ease;
  overflow: hidden;
}
.other-sheet-body-enter,
.other-sheet-body-leave-to {
  max-height: 0;
  opacity: 0;
}
.other-sheet-body-enter-to,
.other-sheet-body-leave {
  max-height: 80vh;
}

.other-downloads__toggle {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: var(--md-text);
}
.other-downloads__heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.other-downloads__toggle .section-title {
  margin: 0;
}
.other-downloads__summary {
  font-size: 12px;
  color: var(--md-text-dim);
}
.other-downloads__toggle .material-icons {
  color: var(--md-text-dim);
}

</style>
